import { useState, useEffect, useRef } from "react"
import { useTareas } from "../context/TareasContext"
import { MdClose } from "react-icons/md"
import { FaCheck, FaTrash, FaExternalLinkAlt, FaPlus } from "react-icons/fa"
import StatusBadge from "./StatusBadge"
import AssigneeAvatar from "./AssigneeAvatar"
import DrawerRight from "../../ui/DrawerRight"
import LoadingScreen from "../../ui/temp-screens/LoadingScreen"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"
import ReactQuill, { Quill } from "react-quill"
import "react-quill/dist/quill.snow.css"

const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]

const normalizeToDelta = (content) => {
  if (!content) return { ops: [{ insert: "\n" }] }
  if (typeof content === "object" && content.ops && Array.isArray(content.ops)) return content
  if (typeof content === "string") {
    try {
      const tempQuill = new Quill(document.createElement("div"))
      tempQuill.clipboard.dangerouslyPasteHTML(content)
      const delta = tempQuill.getContents()
      if (delta?.ops?.length > 0) return delta
      const Delta = Quill.import("delta")
      return new Delta().insert(content + "\n")
    } catch {
      const Delta = Quill.import("delta")
      return new Delta().insert(content + "\n")
    }
  }
  return { ops: [{ insert: "\n" }] }
}

const hasQuillContent = (content) => {
  if (!content) return false
  if (typeof content === "string") return content.trim().length > 0
  if (typeof content === "object" && content.ops && Array.isArray(content.ops)) {
    return content.ops.some(op => typeof op.insert === "string" && op.insert.trim().length > 0)
  }
  return false
}

const formatDate = (date) => {
  if (!date) return "—"
  const d = new Date(date)
  const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"]
  return `${days[d.getDay()]} ${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`
}

const TAG_PRESET_COLORS = ["#6B7280", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"]

const TaskDetailDrawer = () => {
  const {
    selectedTask, detailDrawerOpen, closeDetailDrawer, updateTask, deleteTask, workers,
    getTaskStatusValue, getStatusId, estadosTareas, selectedProject, updateProject,
  } = useTareas()
  const [localTask, setLocalTask] = useState(null)
  const [saving, setSaving] = useState(false)
  const [newSubtask, setNewSubtask] = useState("")
  const [newLink, setNewLink] = useState("")
  const [newTagName, setNewTagName] = useState("")
  const [newTagColor, setNewTagColor] = useState("#6B7280")
  const [creatingTag, setCreatingTag] = useState(false)
  const adminNotesTimer = useRef(null)
  const localTaskRef = useRef(localTask)
  localTaskRef.current = localTask

  useEffect(() => {
    if (selectedTask) setLocalTask({ ...selectedTask })
  }, [selectedTask])

  useEffect(() => {
    if (!detailDrawerOpen) {
      setNewTagName("")
      setNewTagColor("#6B7280")
    }
  }, [detailDrawerOpen])

  if (!detailDrawerOpen) return null

  const save = async (field, value) => {
    setSaving(true)
    try {
      await updateTask(localTask.id, { [field]: value })
    } catch {
      // manejado en context
    } finally {
      setSaving(false)
    }
  }

  const handleField = (field, value) => {
    setLocalTask(p => ({ ...p, [field]: value }))
  }

  const handleAdminNotesChange = (content, delta, source, editor) => {
    if (source !== "user") return
    const deltaContent = editor.getContents()
    setLocalTask(p => ({ ...p, adminNotes: deltaContent }))
    clearTimeout(adminNotesTimer.current)
    adminNotesTimer.current = setTimeout(() => {
      save("adminNotes", deltaContent)
    }, 1000)
  }

  const handleDelete = async () => {
    const ok = await deleteTask(localTask.id)
    if (ok) closeDetailDrawer()
  }

  // Subtareas
  const subtasks = localTask?.subtasks || []
  const completedCount = subtasks.filter(s => s.completed).length
  const progress = subtasks.length > 0 ? Math.round((completedCount / subtasks.length) * 100) : 0

  const addSubtask = () => {
    if (!newSubtask.trim()) return
    const updated = [...subtasks, { text: newSubtask.trim(), completed: false }]
    handleField("subtasks", updated)
    save("subtasks", updated)
    setNewSubtask("")
  }

  const toggleSubtask = (i) => {
    const updated = subtasks.map((s, idx) => idx === i ? { ...s, completed: !s.completed } : s)
    handleField("subtasks", updated)
    save("subtasks", updated)
  }

  const removeSubtask = (i) => {
    const updated = subtasks.filter((_, idx) => idx !== i)
    handleField("subtasks", updated)
    save("subtasks", updated)
  }

  // Links
  const links = localTask?.adminLinks || []
  const addLink = () => {
    if (!newLink.trim()) return
    const updated = [...links, { link: newLink.trim() }]
    handleField("adminLinks", updated)
    save("adminLinks", updated)
    setNewLink("")
  }
  const removeLink = (i) => {
    const updated = links.filter((_, idx) => idx !== i)
    handleField("adminLinks", updated)
    save("adminLinks", updated)
  }

  const normalizeProjectTagsForApi = (tags) =>
    (tags || [])
      .filter((t) => t && t.id)
      .map((t) => ({
        id: String(t.id),
        name: (t.name || "").trim() || "Sin nombre",
        color: typeof t.color === "string" && t.color ? t.color : "#6B7280",
        archived: Boolean(t.archived),
      }))

  const handleCreateProjectTagAndAssign = async () => {
    if (!selectedProject?.id || !localTask?.id) return
    const name = newTagName.trim()
    if (!name) return
    const uuid =
      typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `tag_${Date.now()}_${Math.random().toString(16).slice(2)}`
    const newTag = {
      id: uuid,
      name,
      color: newTagColor || "#6B7280",
      archived: false,
    }
    const nextTags = [...normalizeProjectTagsForApi(selectedProject?.tags), newTag]
    setCreatingTag(true)
    try {
      const doc = await updateProject(selectedProject.id, { tags: nextTags })
      if (!doc) return
      const selectedIds = (localTask.projectTags || [])
        .map((x) => (typeof x === "object" && x != null ? x.tagId : x))
        .filter(Boolean)
      const mergedIds = [...new Set([...selectedIds, uuid])]
      const nextProjectTags = mergedIds.map((id) => ({ tagId: id }))
      await updateTask(localTask.id, { projectTags: nextProjectTags })
      handleField("projectTags", nextProjectTags)
      setNewTagName("")
      setNewTagColor("#6B7280")
    } finally {
      setCreatingTag(false)
    }
  }

  return (
    <DrawerRight
      open={detailDrawerOpen}
      onClose={closeDetailDrawer}
      title={localTask?.task || "Detalle de tarea"}
      maxWidth="720px"
      zIndex={9999}
    >
      {!localTask ? (
        <LoadingScreen />
      ) : (
        <>
          {/* Título editable */}
          <div className="py-4 border-b mb-4 border-gray-50">
            <input 
              type="text"
              value={localTask.task || ""}
              onChange={e => handleField("task", e.target.value)}
              onBlur={e => save("task", e.target.value)}
              rows={2}
              placeholder="Nombre de la tarea..."
              className="w-full text-lg font-semibold text-gray-900 resize-none border-none outline-none focus:ring-0 p-0"
            />
          </div>

          {/* Barra superior: estado, guardando, eliminar */}
          <div className="flex items-center justify-between border-gray-100">
            <div className="flex items-center gap-2">
              <StatusBadge status={getTaskStatusValue(localTask) || localTask.status} />
              {saving && <span className="text-xs text-gray-400 animate-pulse">Guardando...</span>}
            </div>
            <button
              onClick={handleDelete}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Eliminar tarea"
            >
              <FaTrash className="text-xs" />
            </button>
          </div>

          {/* Propiedades */}
          <div className="px-5 py-4 border-b border-gray-100 space-y-3">

            <PropRow label="Estado">
              <select
                value={getTaskStatusValue(localTask) || ""}
                onChange={e => {
                  const val = e.target.value
                  const statusId = getStatusId(val)
                  if (statusId) {
                    handleField("status", statusId)
                    save("status", statusId)
                  }
                }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300 w-full"
              >
                {estadosTareas.map(e => (
                  <option key={e.id} value={e.name}>{e.name}</option>
                ))}
              </select>
            </PropRow>

            <PropRow label="Prioridad">
              <select
                value={localTask.priority || ""}
                onChange={e => {
                  const val = e.target.value
                  handleField("priority", val)
                  save("priority", val)
                }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300 w-full"
              >
                <option value="">Sin prioridad</option>
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </PropRow>

            {selectedProject?.id && (
              <PropRow label="Tags del proyecto">
              <div className="flex flex-col gap-2 w-full">
                <p className="text-[10px] text-gray-500 leading-snug">
                  Los tags son del <strong className="font-medium text-gray-600">proyecto</strong>: al crear uno aquí queda disponible en el resto de tareas. Se asigna automáticamente a esta tarea.
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleCreateProjectTagAndAssign()
                      }
                    }}
                    placeholder="Nombre del nuevo tag…"
                    className="text-xs flex-1 min-w-[140px] border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    disabled={creatingTag}
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                    {TAG_PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        disabled={creatingTag}
                        onClick={() => setNewTagColor(c)}
                        className="w-5 h-5 rounded-full border-2 flex-shrink-0 transition-transform disabled:opacity-40"
                        style={{
                          backgroundColor: c,
                          borderColor: newTagColor === c ? "#1F2937" : "transparent",
                          transform: newTagColor === c ? "scale(1.15)" : "scale(1)",
                        }}
                        title={c}
                      />
                    ))}
                    <input
                      type="color"
                      value={newTagColor}
                      onChange={(e) => setNewTagColor(e.target.value)}
                      disabled={creatingTag}
                      className="w-7 h-7 rounded cursor-pointer border border-gray-200 p-0 flex-shrink-0 disabled:opacity-40"
                      title="Color"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={creatingTag || !newTagName.trim()}
                    onClick={handleCreateProjectTagAndAssign}
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <FaPlus className="text-[9px]" />
                    {creatingTag ? "Creando…" : "Crear y asignar"}
                  </button>
                </div>
                {(() => {
                  const defs = selectedProject?.tags || []
                  const activeDefs = defs.filter((t) => t && !t.archived)
                  const selectedIds = (localTask.projectTags || [])
                    .map((x) => (typeof x === "object" && x != null ? x.tagId : x))
                    .filter(Boolean)
                  const toggleTag = (tagId) => {
                    const set = new Set(selectedIds)
                    if (set.has(tagId)) set.delete(tagId)
                    else set.add(tagId)
                    const next = [...set].map((id) => ({ tagId: id }))
                    handleField("projectTags", next)
                    save("projectTags", next)
                  }
                  return (
                    <>
                      {activeDefs.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100">
                          {activeDefs.map((tag) => {
                            const on = selectedIds.includes(tag.id)
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors ${
                                  on
                                    ? "border-orange-400 bg-orange-50 text-orange-900 font-medium"
                                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                              >
                                <span
                                  className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                                  style={{ backgroundColor: tag.color || "#6B7280" }}
                                />
                                {tag.name}
                              </button>
                            )
                          })}
                        </div>
                      )}
                      {selectedIds.some((id) => defs.find((t) => t.id === id)?.archived) && (
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] text-gray-500">Archivados en la tarea:</span>
                          {selectedIds
                            .filter((id) => defs.find((t) => t.id === id)?.archived)
                            .map((id) => {
                              const d = defs.find((t) => t.id === id)
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => toggleTag(id)}
                                  className="text-[10px] px-2 py-0.5 rounded-md border border-dashed border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100"
                                  title="Quitar de la tarea"
                                >
                                  {d?.name || id}
                                  <span className="text-[9px] text-amber-700"> · archivado</span>
                                </button>
                              )
                            })}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
              </PropRow>
            )}

            <PropRow label="Fecha límite">
              <input
                type="datetime-local"
                value={localTask.limitDate ? localTask.limitDate.slice(0,16) : ""}
                onChange={e => handleField("limitDate", e.target.value)}
                onBlur={e => save("limitDate", e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </PropRow>

            <PropRow label="Asignados">
              <div className="flex flex-col gap-2 w-full">
                {(() => {
                  const assignees = localTask.assignees || []
                  const assigneeIds = assignees.map(a => (typeof a === "object" ? a.id : a)).filter(Boolean)
                  const availableWorkers = workers.filter(w => !assigneeIds.includes(w.id))
                  const addAssignee = (workerId) => {
                    if (!workerId) return
                    const next = [...assigneeIds, workerId]
                    handleField("assignees", next)
                    save("assignees", next)
                  }
                  const removeAssignee = (workerId) => {
                    const next = assigneeIds.filter(id => id !== workerId)
                    handleField("assignees", next)
                    save("assignees", next)
                  }
                  return (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {assignees.map(a => {
                          const id = typeof a === "object" ? a.id : a
                          const name = typeof a === "object" ? a.name : workers.find(w => w.id === id)?.name || id
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs"
                            >
                              <AssigneeAvatar name={name} size={18} user={typeof a === "object" ? a : workers.find(w => w.id === id)} />
                              {formatWorkerDisplayName(name)}
                              <button
                                type="button"
                                onClick={() => removeAssignee(id)}
                                className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-red-500"
                              >
                                <MdClose className="text-[10px]" />
                              </button>
                            </span>
                          )
                        })}
                      </div>
                      {availableWorkers.length > 0 && (
                        <select
                          value=""
                          onChange={e => { addAssignee(e.target.value); e.target.value = "" }}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-full focus:outline-none focus:ring-2 focus:ring-orange-300"
                        >
                          <option value="">+ Añadir asignado...</option>
                          {availableWorkers.map(w => (
                            <option key={w.id} value={w.id}>{formatWorkerDisplayName(w.name || w.email) || w.id}</option>
                          ))}
                        </select>
                      )}
                    </>
                  )
                })()}
              </div>
            </PropRow>

            <PropRow label="Creado por">
              <div className="flex items-center gap-2">
                {localTask.createdBy && (
                  <AssigneeAvatar name={localTask.createdBy?.name || "?"} size={22} user={localTask.createdBy} />
                )}
                <span className="text-xs text-gray-600">{localTask.createdBy?.name ? formatWorkerDisplayName(localTask.createdBy.name) : "—"}</span>
              </div>
            </PropRow>

            <PropRow label="Fecha entrega">
              <span className="text-xs text-gray-600">{formatDate(localTask.deliverDate)}</span>
            </PropRow>

          </div>

          {/* Notas admin */}
          <Section title="Detalles">
            <style>{`
              .admin-notes-editor .ql-container { font-size: 12px; border-radius: 0 0 8px 8px; border-color: #E5E7EB; min-height: 80px; }
              .admin-notes-editor .ql-toolbar { border-radius: 8px 8px 0 0; border-color: #E5E7EB; padding: 4px; }
              .admin-notes-editor .ql-toolbar button { width: 24px; height: 24px; }
              .admin-notes-editor .ql-editor { padding: 8px 10px; min-height: 80px; }
              .admin-notes-editor .ql-editor.ql-blank::before { font-style: normal; color: #9CA3AF; font-size: 12px; }
              .worker-notes-readonly .ql-container { border: none; font-size: 12px; }
              .worker-notes-readonly .ql-editor { padding: 0; min-height: auto; color: #374151; }
            `}</style>
            <div className="admin-notes-editor">
              <ReactQuill
                theme="snow"
                value={normalizeToDelta(localTask.adminNotes)}
                onChange={handleAdminNotesChange}
                placeholder="Añadir notas..."
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ color: [] }],
                    ["link"],
                    ["clean"],
                  ],
                }}
                formats={["bold", "italic", "underline", "list", "bullet", "color", "link"]}
              />
            </div>
          </Section>

          {/* Notas trabajador */}
          {hasQuillContent(localTask.workerNotes) && (
            <Section title="Notas del trabajador">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 worker-notes-readonly">
                <ReactQuill
                  theme="snow"
                  value={normalizeToDelta(localTask.workerNotes)}
                  readOnly
                  modules={{ toolbar: false }}
                />
              </div>
            </Section>
          )}

          {/* Subtareas */}
          <Section title={`Subtareas${subtasks.length > 0 ? ` (${completedCount}/${subtasks.length})` : ""}`}>
            {subtasks.length > 0 && (
              <>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
                  <div
                    className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="space-y-1.5 mb-3">
                  {subtasks.map((sub, i) => (
                    <div key={i} className="flex items-center gap-2 group">
                      <button
                        onClick={() => toggleSubtask(i)}
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                          sub.completed
                            ? "bg-green-500 border-green-500 text-white"
                            : "border-gray-300 hover:border-green-400"
                        }`}
                      >
                        {sub.completed && <FaCheck className="text-[8px]" />}
                      </button>
                      <span className={`text-xs flex-1 ${sub.completed ? "line-through text-gray-400" : "text-gray-700"}`}>
                        {sub.text}
                      </span>
                      <button
                        onClick={() => removeSubtask(i)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-all"
                      >
                        <MdClose className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSubtask}
                onChange={e => setNewSubtask(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addSubtask()}
                placeholder="Nueva subtarea..."
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <button onClick={addSubtask} className="p-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                <FaPlus className="text-xs" />
              </button>
            </div>
          </Section>

          {/* Links */}
          <Section title="Links">
            {links.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {links.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 group">
                    <a
                      href={l.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-xs text-blue-600 hover:underline truncate"
                    >
                      {l.link}
                    </a>
                    <button
                      onClick={() => window.open(l.link, "_blank")}
                      className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-blue-600"
                    >
                      <FaExternalLinkAlt className="text-[10px]" />
                    </button>
                    <button
                      onClick={() => removeLink(i)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500"
                    >
                      <MdClose className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {links.length < 5 && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newLink}
                  onChange={e => setNewLink(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addLink()}
                  placeholder="https://..."
                  className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
                <button onClick={addLink} className="p-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                  <FaPlus className="text-xs" />
                </button>
              </div>
            )}
          </Section>

          {/* Audios */}
          {localTask.audios?.length > 0 && (
            <Section title="Audios">
              <div className="space-y-3">
                {localTask.audios.map((audio, i) => (
                  audio.mimeType === "audio/wav" && (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700 truncate">{audio.filename}</span>
                        <button onClick={() => window.open(audio.url, "_blank")} className="text-blue-500 hover:text-blue-700">
                          <FaExternalLinkAlt className="text-xs" />
                        </button>
                      </div>
                      <audio controls className="w-full h-8">
                        <source src={audio.url} type={audio.mimeType} />
                      </audio>
                    </div>
                  )
                ))}
              </div>
            </Section>
          )}

          {/* Footer con metadata */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-[10px] text-gray-400">
              <span>ID: {localTask.id?.slice(0,8)}...</span>
              <span>Creado: {formatDate(localTask.createdAt)}</span>
            </div>
          </div>
        </>
      )}
    </DrawerRight>
  )
}

const PropRow = ({ label, children }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-500 w-28 flex-shrink-0">{label}</span>
    {children}
  </div>
)

const Section = ({ title, children }) => (
  <div className="py-4 border-b border-gray-100">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
    {children}
  </div>
)

export default TaskDetailDrawer
